/**
 * Element Picker Script for Shopify Theme
 *
 * Add this to your Shopify theme to enable element picking from external dashboard.
 * Upload to: Assets > element-picker-iframe.js
 * Then add to theme.liquid: <script src="{{ 'element-picker-iframe.js' | asset_url }}" defer></script>
 */

/**
 * Injectable Script for Cross-Origin Element Picker
 *
 * This script runs inside the iframe and sends element information
 * to the parent window via postMessage.
 */

(function() {
  'use strict';

  const PICKER_MESSAGE_PREFIX = 'ELEMENT_PICKER:';
  let isActive = false;
  let lastHoveredElement = null;
  let cursorStyleElement = null;

  // Helper: Check if element is valid target
  function isValidTarget(element) {
    if (!element || !(element instanceof HTMLElement)) return false;

    // Exclude script, style, etc.
    if (['SCRIPT', 'STYLE', 'NOSCRIPT', 'LINK', 'META'].includes(element.tagName)) {
      return false;
    }

    // Exclude picker UI
    if (element.dataset.pickerUi !== undefined) {
      return false;
    }

    // Check minimum size
    const rect = element.getBoundingClientRect();
    if (rect.width < 10 || rect.height < 10) {
      return false;
    }

    // Check if visible
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    return true;
  }

  // Helper: Find nearest valid target by traversing up the DOM tree
  function findNearestValidTarget(element) {
    if (!element || !(element instanceof HTMLElement)) return null;

    const interactiveElements = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL'];
    let current = element;
    let depth = 0;
    const maxDepth = 5;

    while (current && current !== document.body && depth < maxDepth) {
      // Prioritize interactive elements even if the original element was valid
      if (interactiveElements.includes(current.tagName) && isValidTarget(current)) {
        return current;
      }

      // Otherwise, use first valid target
      if (isValidTarget(current)) {
        return current;
      }

      current = current.parentElement;
      depth++;
    }

    return null;
  }

  // Helper: Generate CSS selector
  function generateSelector(element) {
    // Try ID first
    if (element.id) {
      return '#' + CSS.escape(element.id);
    }

    // Build path with classes
    const parts = [];
    let current = element;
    let depth = 0;
    const maxDepth = 3;

    while (current && depth < maxDepth) {
      let part = current.tagName.toLowerCase();

      if (current.classList.length > 0) {
        const classes = Array.from(current.classList)
          .filter(cls => !cls.match(/^js-/) && cls.length < 30)
          .slice(0, 2)
          .map(cls => '.' + CSS.escape(cls))
          .join('');
        part += classes;
      }

      parts.unshift(part);
      current = current.parentElement;
      depth++;
    }

    return parts.join(' > ');
  }

  // Helper: Detect Shopify metadata
  function detectShopifyMetadata(element) {
    const metadata = {
      sectionId: null,
      blockId: null,
      sectionName: null,
      liquidFile: null
    };

    let current = element;
    while (current) {
      if (!metadata.sectionId && current.dataset.sectionId) {
        metadata.sectionId = current.dataset.sectionId;
        const parts = current.dataset.sectionId.split('--');
        metadata.sectionName = parts[parts.length - 1];
        metadata.liquidFile = 'sections/' + metadata.sectionName + '.liquid';
      }

      if (!metadata.blockId && current.dataset.blockId) {
        metadata.blockId = current.dataset.blockId;
      }

      current = current.parentElement;
    }

    return metadata;
  }

  // Helper: Analyze element
  function analyzeElement(element) {
    const rect = element.getBoundingClientRect();
    const computed = window.getComputedStyle(element);
    const shopify = detectShopifyMetadata(element);

    return {
      cssSelector: generateSelector(element),
      tagName: element.tagName.toLowerCase(),
      id: element.id || null,
      classes: Array.from(element.classList),
      textContent: (element.textContent || '').trim().substring(0, 200),
      innerText: (element.innerText || '').trim().substring(0, 200),

      boundingRect: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        x: rect.x,
        y: rect.y
      },

      shopify: shopify,

      styles: {
        color: computed.color,
        backgroundColor: computed.backgroundColor,
        fontSize: computed.fontSize,
        fontFamily: computed.fontFamily,
        fontWeight: computed.fontWeight,
        display: computed.display,
        padding: computed.padding,
        margin: computed.margin
      },

      hasImages: element.querySelector('img') !== null,
      hasText: (element.textContent || '').trim().length > 0
    };
  }

  // Handle mouse move
  function handleMouseMove(e) {
    if (!isActive) return;

    const detectedElement = document.elementFromPoint(e.clientX, e.clientY);
    const element = findNearestValidTarget(detectedElement);

    if (!element) {
      if (lastHoveredElement) {
        window.parent.postMessage({
          type: PICKER_MESSAGE_PREFIX + 'HOVER',
          element: null
        }, '*');
        lastHoveredElement = null;
      }
      return;
    }

    if (element !== lastHoveredElement) {
      lastHoveredElement = element;

      const rect = element.getBoundingClientRect();
      const tagName = element.tagName.toLowerCase();
      const classes = Array.from(element.classList).slice(0, 2);

      window.parent.postMessage({
        type: PICKER_MESSAGE_PREFIX + 'HOVER',
        element: {
          tagName: tagName,
          classes: classes,
          rect: {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            x: rect.x,
            y: rect.y
          }
        }
      }, '*');
    }
  }

  // Handle click
  function handleClick(e) {
    if (!isActive) return;

    e.preventDefault();
    e.stopPropagation();

    const detectedElement = document.elementFromPoint(e.clientX, e.clientY);
    const element = findNearestValidTarget(detectedElement);

    if (!element) return;

    const elementInfo = analyzeElement(element);

    window.parent.postMessage({
      type: PICKER_MESSAGE_PREFIX + 'SELECT',
      element: elementInfo
    }, '*');
  }

  // Handle activate/deactivate messages from parent
  window.addEventListener('message', function(event) {
    if (event.data.type === PICKER_MESSAGE_PREFIX + 'ACTIVATE') {
      isActive = true;
      document.body.dataset.pickerActive = 'true';

      // Inject style to force crosshair cursor on all elements
      cursorStyleElement = document.createElement('style');
      cursorStyleElement.dataset.pickerUi = 'cursor-override';
      cursorStyleElement.textContent = '* { cursor: crosshair !important; }';
      document.head.appendChild(cursorStyleElement);

      console.log('🎯 Element picker activated in iframe');
    } else if (event.data.type === PICKER_MESSAGE_PREFIX + 'DEACTIVATE') {
      isActive = false;
      delete document.body.dataset.pickerActive;
      lastHoveredElement = null;

      // Remove cursor override style
      if (cursorStyleElement && cursorStyleElement.parentNode) {
        cursorStyleElement.parentNode.removeChild(cursorStyleElement);
        cursorStyleElement = null;
      }

      console.log('🎯 Element picker deactivated in iframe');

      window.parent.postMessage({
        type: PICKER_MESSAGE_PREFIX + 'HOVER',
        element: null
      }, '*');
    }
  });

  // Attach event listeners
  document.addEventListener('mousemove', handleMouseMove, true);
  document.addEventListener('click', handleClick, true);

  // Notify parent that script is ready
  window.parent.postMessage({
    type: PICKER_MESSAGE_PREFIX + 'READY'
  }, '*');

  console.log('🎯 Element picker script loaded in iframe');

  // ============================================================================
  // URL Navigation Tracking
  // ============================================================================

  let lastTrackedUrl = window.location.href;

  // Send initial URL to parent
  function sendCurrentUrl() {
    const navData = {
      type: 'PAGE_NAVIGATION',
      url: window.location.href,
      pathname: window.location.pathname
    };
    console.log('📤 Sending PAGE_NAVIGATION:', navData);
    window.parent.postMessage(navData, '*');
  }

  // Send initial URL
  console.log('🚀 Sending initial PAGE_NAVIGATION message...');
  sendCurrentUrl();

  // Track URL changes via popstate (browser back/forward)
  window.addEventListener('popstate', function() {
    const currentUrl = window.location.href;
    if (currentUrl !== lastTrackedUrl) {
      lastTrackedUrl = currentUrl;
      sendCurrentUrl();
      console.log('🔗 URL changed (popstate):', currentUrl);
    }
  });

  // Track URL changes via pushState/replaceState (for SPAs)
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function() {
    originalPushState.apply(this, arguments);
    const currentUrl = window.location.href;
    if (currentUrl !== lastTrackedUrl) {
      lastTrackedUrl = currentUrl;
      sendCurrentUrl();
      console.log('🔗 URL changed (pushState):', currentUrl);
    }
  };

  history.replaceState = function() {
    originalReplaceState.apply(this, arguments);
    const currentUrl = window.location.href;
    if (currentUrl !== lastTrackedUrl) {
      lastTrackedUrl = currentUrl;
      sendCurrentUrl();
      console.log('🔗 URL changed (replaceState):', currentUrl);
    }
  };
})();
